/**
 * useAdminAdvisors Hooks
 * React Query hooks for financial advisor management with optimistic updates
 * 
 * Implements T128 and T129:
 * - useAdminAdvisors: List advisors with pagination and search
 * - useUpdateAdvisor: Update advisor mutation
 * - useDeleteAdvisor: Delete advisor mutation
 * 
 * Note: No create mutation - advisors are created via user registration flow
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminAdvisorsApi } from '../services/adminAdvisorsApi';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminAdvisor,
  AdminAdvisorEditorDetail,
  AdminAdvisorEditorUpdatePayload,
  UpdateAdvisorPayload,
  AdvisorListParams,
} from '../types/admin-advisor.type';

// Query keys for cache management
export const advisorKeys = {
  all: ['admin', 'advisors'] as const,
  lists: () => [...advisorKeys.all, 'list'] as const,
  list: (params: AdvisorListParams) => [...advisorKeys.lists(), params] as const,
  details: () => [...advisorKeys.all, 'detail'] as const,
  detail: (id: string) => [...advisorKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated advisors list
 * 
 * @param params - Query parameters (page, pageSize, search)
 * @returns Query result with paginated advisors
 * 
 * Features:
 * - 30s stale time to reduce API calls
 * - keepPreviousData for smooth pagination
 * - Total count reflects filtered results
 * 
 * @example
 * const { data, isLoading, error } = useAdminAdvisors({
 *   page: 1,
 *   pageSize: 50,
 *   search: 'smith'
 * });
 */
export function useAdminAdvisors(
  params: AdvisorListParams = {},
): UseQueryResult<PaginatedResponse<AdminAdvisor>, Error> {
  return useQuery({
    queryKey: advisorKeys.list(params),
    queryFn: () => adminAdvisorsApi.listAdvisors(params),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data during refetch
  });
}

/**
 * Hook to fetch single advisor by ID
 * 
 * @param id - Advisor ID
 * @returns Query result with advisor details
 */
export function useAdminAdvisor(
  id: string,
): UseQueryResult<AdminAdvisor, Error> {
  return useQuery({
    queryKey: advisorKeys.detail(id),
    queryFn: () => adminAdvisorsApi.getAdvisor(id),
    enabled: !!id,
  });
}

export function useAdminAdvisorEditor(
  id: string,
): UseQueryResult<AdminAdvisorEditorDetail, Error> {
  return useQuery({
    queryKey: advisorKeys.detail(id),
    queryFn: () => adminAdvisorsApi.getAdvisorEditorDetail(id),
    enabled: !!id,
  });
}

/**
 * Hook to update existing advisor
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic update (immediate UI update)
 * - Rollback on error
 * - Updates cache with new data
 * 
 * @example
 * const updateAdvisor = useUpdateAdvisor();
 * 
 * await updateAdvisor.mutateAsync({
 *   id: 'advisor_123',
 *   payload: { companyName: 'New Firm Name' }
 * });
 */
export function useUpdateAdvisor(): UseMutationResult<
  AdminAdvisor,
  Error,
  { id: string; payload: UpdateAdvisorPayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => adminAdvisorsApi.updateAdvisor(id, payload),
    
    // Optimistic update
    onMutate: async ({ id, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: advisorKeys.detail(id) });

      // Snapshot previous value
      const previousAdvisor = queryClient.getQueryData<AdminAdvisor>(
        advisorKeys.detail(id),
      );

      // Optimistically update cache
      if (previousAdvisor) {
        queryClient.setQueryData<AdminAdvisor>(advisorKeys.detail(id), {
          ...previousAdvisor,
          ...payload,
        });
      }

      return { previousAdvisor };
    },
    
    // Rollback on error
    onError: (_error, { id }, context) => {
      if (context?.previousAdvisor) {
        queryClient.setQueryData(advisorKeys.detail(id), context.previousAdvisor);
      }
    },
    
    // Refetch on success
    onSuccess: (data, { id }) => {
      // Update detail cache with server response
      queryClient.setQueryData(advisorKeys.detail(id), data);
      
      // Invalidate lists to show updated advisor
      queryClient.invalidateQueries({ queryKey: advisorKeys.lists() });
    },
  });
}

function applyEditorPayload(
  advisor: AdminAdvisorEditorDetail,
  payload: AdminAdvisorEditorUpdatePayload,
): AdminAdvisorEditorDetail {
  return {
    ...advisor,
    ...payload,
    user: {
      ...advisor.user,
      firstName: payload.firstName ?? advisor.user.firstName,
      lastName: payload.lastName ?? advisor.user.lastName,
      email: payload.email ?? advisor.user.email,
      accountStatus: payload.accountStatus ?? advisor.user.accountStatus,
      profilePhoto: payload.profilePhoto ?? advisor.user.profilePhoto,
      partnerCompanyId: payload.partnerCompanyId ?? advisor.user.partnerCompanyId,
    },
  };
}

export function useSaveAdminAdvisor(): UseMutationResult<
  AdminAdvisorEditorDetail,
  Error,
  { id: string; payload: AdminAdvisorEditorUpdatePayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => adminAdvisorsApi.saveAdvisorEditor(id, payload),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: advisorKeys.detail(id) });
      const previousAdvisor = queryClient.getQueryData<AdminAdvisorEditorDetail>(
        advisorKeys.detail(id),
      );

      if (previousAdvisor) {
        queryClient.setQueryData<AdminAdvisorEditorDetail>(
          advisorKeys.detail(id),
          applyEditorPayload(previousAdvisor, payload),
        );
      }

      return { previousAdvisor };
    },
    onError: (_error, { id }, context) => {
      if (context?.previousAdvisor) {
        queryClient.setQueryData(advisorKeys.detail(id), context.previousAdvisor);
      }
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(advisorKeys.detail(id), data);
      queryClient.invalidateQueries({ queryKey: advisorKeys.lists() });
    },
  });
}

/**
 * Hook to soft-delete advisor
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic removal from lists
 * - Rollback on error
 * - Invalidates all related queries
 * 
 * @example
 * const deleteAdvisor = useDeleteAdvisor();
 * 
 * await deleteAdvisor.mutateAsync('advisor_123');
 */
export function useDeleteAdvisor(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminAdvisorsApi.softDeleteAdvisor,
    
    // Optimistic update - remove from lists
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: advisorKeys.lists() });

      // Snapshot all list queries
      const previousLists = queryClient.getQueriesData<
        PaginatedResponse<AdminAdvisor>
      >({
        queryKey: advisorKeys.lists(),
      });

      // Optimistically remove from all cached lists
      queryClient.setQueriesData<PaginatedResponse<AdminAdvisor>>(
        { queryKey: advisorKeys.lists() },
        (old) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.filter((advisor) => advisor.id !== id),
            meta: {
              ...old.meta,
              total: old.meta.total - 1,
            },
          };
        },
      );

      return { previousLists };
    },
    
    // Rollback on error
    onError: (_error, _id, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    
    // Refetch on success
    onSuccess: (_data, id) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: advisorKeys.detail(id) });
      
      // Invalidate all lists to get fresh totals
      queryClient.invalidateQueries({ queryKey: advisorKeys.lists() });
    },
  });
}
